import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { RecordNavigationComponent } from '../_shared/record-navigation/record-navigation.component';
import { ExpectedDeliveryApprovalDialogComponent } from './expected-delivery-approval-dialog';
import { ExpectedDeliveryItemDialogComponent, ItemDialogSaveData } from './expected-delivery-item-dialog';
import { ExpectedDeliveryService } from '../../_services/expected-delivery.service';
import { ProjectService, CustomerService, TicketService, ArticleTemplateService, WarehouseService } from '../../_services';
import { ExpectedDelivery, ExpectedDeliveryReceipt, ExpectedDeliveryItem, HANDLING_INSTRUCTIONS, DELIVERY_STATUS } from '../../_models/expected-delivery.interface';
import { APProject } from '../../_models/project.interface';
import { Customer, ArticleTemplate } from '../../_models';
import { DatePipe } from '@angular/common';

interface TrackingEntry {
  carrier: string;
  number: string;
}

@Component({
  selector: 'hawk-expected-deliveries-show',
  standalone: true,
  imports: [
    RecordNavigationComponent,
    DatePipe,
    FormsModule,
    RouterLink,
    MatDatepickerModule,
    MatNativeDateModule,
    ExpectedDeliveryApprovalDialogComponent,
    ExpectedDeliveryItemDialogComponent,
  ],
  templateUrl: './expected-deliveries-show.component.html',
  host: {
    class: 'app-page'
  }
})
export class ExpectedDeliveriesShowComponent implements OnInit {
  delivery: ExpectedDelivery | null = null;
  history: ExpectedDeliveryReceipt[] = [];
  isLoading = true;
  errorMessage = '';
  showCancelConfirm = false;
  showDeleteConfirm = false;
  selectedReceipt: ExpectedDeliveryReceipt | null = null;

  // Tracking Numbers
  carriers = ['DHL', 'Hermes', 'UPS', 'DPD', 'GLS', 'FedEx', 'Andere'];
  isEditingTracking = false;
  isSavingTracking = false;
  trackingNumbers: TrackingEntry[] = [];

  // E-Mail Benachrichtigungen
  isEditingNotifications = false;
  isSavingNotifications = false;
  editInternalEmails = '';
  editExternalEmails = '';

  // Lieferzeitraum
  isEditingDeliveryPeriod = false;
  isSavingDeliveryPeriod = false;
  editExpectedFrom: Date | null = null;
  editExpectedUntil: Date | null = null;

  // Hinweise für Lageristen
  isEditingNotes = false;
  isSavingNotes = false;
  editNotes = '';

  // Handlungsanweisung
  isEditingHandlingInstruction = false;
  isSavingHandlingInstruction = false;
  editHandlingInstruction = '';
  showCustomHandlingInstruction = false;
  handlingInstructionOptions = [
    { value: HANDLING_INSTRUCTIONS.STOCK_IN, label: 'Einlagerung ins Hauptlager' },
    { value: HANDLING_INSTRUCTIONS.FORWARD, label: 'Weiterversand (an Techniker/Kunden)' },
    { value: 'OTHER', label: 'Andere (Freitext)' }
  ];

  // Stammdaten Inline-Editing
  projects: APProject[] = [];
  customers: Customer[] = [];
  projectsLoaded = false;
  customersLoaded = false;

  // Aktives Editing-Feld (nur eins gleichzeitig)
  editingField: string | null = null;
  isSavingField = false;

  // Edit-Werte für Stammdaten
  editTicketNumber = '';
  ticketCheckStatus: 'idle' | 'checking' | 'found' | 'not_found' = 'idle';
  foundTicketId: number | null = null;
  foundTicketOrderNr: string | null = null;

  editProjectId: number | null = null;
  editCustomerId: number | null = null;
  editPackagingType: string = '';
  editOrderDate: Date | null = null;

  // Approval
  isApproving = false;
  approvalError = '';
  showApprovalDialog = false;

  // Item Dialog
  showItemDialog = false;
  itemDialogMode: 'create' | 'edit' = 'create';
  selectedItem: ExpectedDeliveryItem | null = null;
  isSavingItem = false;

  // Artikelvorlagen (einmalig geladen für alle Dialoge)
  articleTemplates: ArticleTemplate[] = [];

  // Artikelvorlagen-IDs im Quell-Lager (bei internen Lieferungen)
  warehouseArticleTemplateIds: number[] = [];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private expectedDeliveryService: ExpectedDeliveryService,
    private projectService: ProjectService,
    private customerService: CustomerService,
    private ticketService: TicketService,
    private articleTemplateService: ArticleTemplateService,
    private warehouseService: WarehouseService
  ) {}

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.loadDelivery(id);
      this.loadHistory(id);
    }
    // Artikelvorlagen einmalig laden (für Item-Dialog und Approval-Dialog)
    this.loadArticleTemplates();
  }

  private loadArticleTemplates(): void {
    this.articleTemplateService.all().subscribe({
      next: (data) => {
        this.articleTemplates = data;
      },
      error: (err) => console.error('Fehler beim Laden der Artikelvorlagen:', err)
    });
  }

  /**
   * Wird aufgerufen wenn der Picker die Templates neu lädt
   */
  onTemplatesReloaded(templates: ArticleTemplate[]): void {
    this.articleTemplates = templates;
  }

  private loadDelivery(id: string): void {
    this.expectedDeliveryService.getById(id).subscribe({
      next: (data) => {
        this.delivery = data;
        this.isLoading = false;
        // Bei internen Lieferungen: Artikelvorlagen-IDs im Quell-Lager laden
        if (data.source_warehouse_id) {
          this.loadWarehouseArticleTemplateIds(data.source_warehouse_id);
        }
      },
      error: (err) => {
        this.errorMessage = 'Lieferung konnte nicht geladen werden.';
        this.isLoading = false;
        console.error('Fehler:', err);
      }
    });
  }

  /**
   * Lädt die Artikelvorlagen-IDs die im Quell-Lager vorhanden sind
   */
  private loadWarehouseArticleTemplateIds(warehouseId: number): void {
    this.warehouseService.loadArticles(warehouseId).subscribe({
      next: (data) => {
        this.warehouseArticleTemplateIds = data.map((item: any) => item.id);
      },
      error: (err) => console.error('Fehler beim Laden der Lager-Artikelvorlagen:', err)
    });
  }

  private loadHistory(id: string): void {
    this.expectedDeliveryService.getHistory(id).subscribe({
      next: (data) => {
        this.history = data;
      },
      error: (err) => {
        console.error('Fehler beim Laden der Historie:', err);
      }
    });
  }

  getStatusBadgeClass(status: string): string {
    const classMap: Record<string, string> = {
      'open': 'badge-info',
      'partial': 'badge-warning',
      'completed': 'badge-success',
      'cancelled': 'badge-error',
      'unexpected': 'badge-warning',
      'pending_confirmation': 'badge-success',
    };
    return classMap[status] || 'badge-ghost';
  }

  getStatusLabel(status: string): string {
    const labelMap: Record<string, string> = {
      'open': 'Offen',
      'partial': 'Teilweise angekommen',
      'completed': 'Abgeschlossen',
      'cancelled': 'Storniert',
      'unexpected': 'Wartet auf Büro-Freigabe',
      'pending_confirmation': 'Lager-Bestätigung ausstehend',
    };
    return labelMap[status] || status;
  }

  getOwnerLabel(owner: string): string {
    return owner === 'BENTOMAX' ? 'Bentomax' : 'Kunde';
  }

  getPackagingLabel(type: string | null): string {
    if (!type) return '-';
    return type === 'PALLET' ? 'Palette' : 'Paket';
  }

  cancelDelivery(): void {
    if (!this.delivery) return;

    this.expectedDeliveryService.cancel(this.delivery.id).subscribe({
      next: () => {
        this.showCancelConfirm = false;
        this.loadDelivery(this.delivery!.id.toString());
      },
      error: (err) => {
        this.errorMessage = err.error?.error || 'Fehler beim Stornieren.';
        this.showCancelConfirm = false;
      }
    });
  }

  deleteDelivery(): void {
    if (!this.delivery) return;

    this.expectedDeliveryService.delete(this.delivery.id).subscribe({
      next: () => {
        this.router.navigate(['/lager/erwartete-lieferungen']);
      },
      error: (err) => {
        this.errorMessage = err.error?.error || 'Fehler beim Löschen.';
        this.showDeleteConfirm = false;
      }
    });
  }

  canDelete(): boolean {
    if (!this.delivery) return false;
    // Nur löschen wenn keine Wareneingänge gebucht wurden
    return this.delivery.total_received === 0;
  }

  // Tracking Numbers Management

  /**
   * Parst den external_delivery_note_number String zu TrackingEntry Array
   * Format: "DHL:12345;Hermes:67890"
   */
  parseTrackingNumbers(value: string | null | undefined): TrackingEntry[] {
    if (!value) return [];
    return value.split(';')
      .map(entry => entry.trim())
      .filter(entry => entry.length > 0)
      .map(entry => {
        const colonIndex = entry.indexOf(':');
        if (colonIndex > 0) {
          return {
            carrier: entry.substring(0, colonIndex),
            number: entry.substring(colonIndex + 1)
          };
        }
        // Fallback für alte Daten ohne Carrier
        return { carrier: 'Andere', number: entry };
      });
  }

  /**
   * Formatiert TrackingEntry Array zu String
   */
  formatTrackingNumbers(): string {
    return this.trackingNumbers
      .filter(entry => entry.number?.trim())
      .map(entry => `${entry.carrier}:${entry.number.trim()}`)
      .join(';');
  }

  /**
   * Startet den Bearbeitungsmodus für Tracking-Nummern
   */
  startEditingTracking(): void {
    this.trackingNumbers = this.parseTrackingNumbers(this.delivery?.external_delivery_note_number);
    this.isEditingTracking = true;
  }

  /**
   * Bricht die Bearbeitung ab
   */
  cancelEditingTracking(): void {
    this.isEditingTracking = false;
    this.trackingNumbers = [];
  }

  /**
   * Fügt eine neue Tracking-Nummer hinzu
   */
  addTrackingNumber(): void {
    this.trackingNumbers.push({ carrier: 'DHL', number: '' });
  }

  /**
   * Entfernt eine Tracking-Nummer
   */
  removeTrackingNumber(index: number): void {
    this.trackingNumbers.splice(index, 1);
  }

  /**
   * Speichert die Tracking-Nummern
   */
  saveTrackingNumbers(): void {
    if (!this.delivery) return;

    this.isSavingTracking = true;
    const formattedValue = this.formatTrackingNumbers();

    this.expectedDeliveryService.update(this.delivery.id, {
      external_delivery_note_number: formattedValue
    }).subscribe({
      next: (response) => {
        this.delivery = response.expected_delivery;
        this.isEditingTracking = false;
        this.isSavingTracking = false;
        this.trackingNumbers = [];
      },
      error: (err) => {
        this.errorMessage = err.error?.errors?.join(', ') || 'Fehler beim Speichern.';
        this.isSavingTracking = false;
      }
    });
  }

  /**
   * Gibt die geparsten Tracking-Nummern für die Anzeige zurück
   */
  getDisplayTrackingNumbers(): TrackingEntry[] {
    return this.parseTrackingNumbers(this.delivery?.external_delivery_note_number);
  }

  // E-Mail Benachrichtigungen Management

  /**
   * Startet den Bearbeitungsmodus für E-Mail-Benachrichtigungen
   */
  startEditingNotifications(): void {
    this.editInternalEmails = this.delivery?.internal_notification_emails || '';
    this.editExternalEmails = this.delivery?.external_notification_emails || '';
    this.isEditingNotifications = true;
  }

  /**
   * Bricht die Bearbeitung ab
   */
  cancelEditingNotifications(): void {
    this.isEditingNotifications = false;
    this.editInternalEmails = '';
    this.editExternalEmails = '';
  }

  /**
   * Speichert die E-Mail-Benachrichtigungen
   */
  saveNotifications(): void {
    if (!this.delivery) return;

    this.isSavingNotifications = true;

    this.expectedDeliveryService.update(this.delivery.id, {
      internal_notification_emails: this.editInternalEmails || undefined,
      external_notification_emails: this.editExternalEmails || undefined
    }).subscribe({
      next: (response) => {
        this.delivery = response.expected_delivery;
        this.isEditingNotifications = false;
        this.isSavingNotifications = false;
      },
      error: (err) => {
        this.errorMessage = err.error?.error || err.error?.errors?.join(', ') || 'Fehler beim Speichern.';
        this.isSavingNotifications = false;
      }
    });
  }

  // Lieferzeitraum Management

  /**
   * Startet den Bearbeitungsmodus für den Lieferzeitraum
   */
  startEditingDeliveryPeriod(): void {
    this.editExpectedFrom = this.delivery?.expected_from ? new Date(this.delivery.expected_from) : null;
    this.editExpectedUntil = this.delivery?.expected_until ? new Date(this.delivery.expected_until) : null;
    this.isEditingDeliveryPeriod = true;
  }

  /**
   * Bricht die Bearbeitung ab
   */
  cancelEditingDeliveryPeriod(): void {
    this.isEditingDeliveryPeriod = false;
    this.editExpectedFrom = null;
    this.editExpectedUntil = null;
  }

  /**
   * Speichert den Lieferzeitraum
   * Bei Änderung von expected_until wird im Backend das overdue_reminder_sent_at Flag zurückgesetzt
   */
  saveDeliveryPeriod(): void {
    if (!this.delivery) return;

    this.isSavingDeliveryPeriod = true;

    this.expectedDeliveryService.update(this.delivery.id, {
      expected_from: this.formatDate(this.editExpectedFrom),
      expected_until: this.formatDate(this.editExpectedUntil)
    }).subscribe({
      next: (response) => {
        this.delivery = response.expected_delivery;
        this.isEditingDeliveryPeriod = false;
        this.isSavingDeliveryPeriod = false;
        this.editExpectedFrom = null;
        this.editExpectedUntil = null;
      },
      error: (err) => {
        this.errorMessage = err.error?.error || err.error?.errors?.join(', ') || 'Fehler beim Speichern.';
        this.isSavingDeliveryPeriod = false;
      }
    });
  }

  /**
   * Formatiert ein Date-Objekt zu lokalem Datum-String (YYYY-MM-DD) oder null
   */
  private formatDate(date: Date | null): string | null {
    if (!date) return null;
    // Lokales Datum verwenden (nicht UTC), um Timezone-Probleme zu vermeiden
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // Hinweise für Lageristen Management

  /**
   * Startet den Bearbeitungsmodus für die Hinweise
   */
  startEditingNotes(): void {
    this.editNotes = this.delivery?.notes || '';
    this.isEditingNotes = true;
  }

  /**
   * Bricht die Bearbeitung ab
   */
  cancelEditingNotes(): void {
    this.isEditingNotes = false;
    this.editNotes = '';
  }

  /**
   * Speichert die Hinweise
   */
  saveNotes(): void {
    if (!this.delivery) return;

    this.isSavingNotes = true;

    this.expectedDeliveryService.update(this.delivery.id, {
      notes: this.editNotes || null
    }).subscribe({
      next: (response) => {
        this.delivery = response.expected_delivery;
        this.isEditingNotes = false;
        this.isSavingNotes = false;
        this.editNotes = '';
      },
      error: (err) => {
        this.errorMessage = err.error?.error || err.error?.errors?.join(', ') || 'Fehler beim Speichern.';
        this.isSavingNotes = false;
      }
    });
  }

  // Handlungsanweisung Management

  /**
   * Gibt das Label für eine Handlungsanweisung zurück
   */
  getHandlingInstructionLabel(value: string | null | undefined): string {
    if (!value) return '-';
    const option = this.handlingInstructionOptions.find(o => o.value === value);
    return option ? option.label : value; // Freitext wird direkt angezeigt
  }

  /**
   * Startet den Bearbeitungsmodus für die Handlungsanweisung
   */
  startEditingHandlingInstruction(): void {
    const currentValue = this.delivery?.handling_instruction || '';
    const isPredefined = this.handlingInstructionOptions.some(o => o.value === currentValue && o.value !== 'OTHER');

    if (isPredefined) {
      this.editHandlingInstruction = currentValue;
      this.showCustomHandlingInstruction = false;
    } else if (currentValue) {
      // Freitext
      this.editHandlingInstruction = currentValue;
      this.showCustomHandlingInstruction = true;
    } else {
      this.editHandlingInstruction = '';
      this.showCustomHandlingInstruction = false;
    }
    this.isEditingHandlingInstruction = true;
  }

  /**
   * Bricht die Bearbeitung ab
   */
  cancelEditingHandlingInstruction(): void {
    this.isEditingHandlingInstruction = false;
    this.editHandlingInstruction = '';
    this.showCustomHandlingInstruction = false;
  }

  /**
   * Wird aufgerufen wenn sich die Auswahl ändert
   */
  onEditHandlingInstructionChange(event: Event): void {
    const value = (event.target as HTMLSelectElement).value;
    if (value === 'OTHER') {
      this.showCustomHandlingInstruction = true;
      this.editHandlingInstruction = '';
    } else {
      this.showCustomHandlingInstruction = false;
      this.editHandlingInstruction = value;
    }
  }

  /**
   * Speichert die Handlungsanweisung
   */
  saveHandlingInstruction(): void {
    if (!this.delivery) return;

    this.isSavingHandlingInstruction = true;

    this.expectedDeliveryService.update(this.delivery.id, {
      handling_instruction: this.editHandlingInstruction
    }).subscribe({
      next: (response) => {
        this.delivery = response.expected_delivery;
        this.isEditingHandlingInstruction = false;
        this.isSavingHandlingInstruction = false;
        this.editHandlingInstruction = '';
        this.showCustomHandlingInstruction = false;
      },
      error: (err) => {
        this.errorMessage = err.error?.error || err.error?.errors?.join(', ') || 'Fehler beim Speichern.';
        this.isSavingHandlingInstruction = false;
      }
    });
  }

  // Stammdaten Inline-Editing

  /**
   * Lädt die Projekte für das Dropdown (admin_page_hash mit customer_id, auf aktive gefiltert)
   */
  private loadProjects(): void {
    if (this.projectsLoaded) return;
    this.projectService.getAPProjects().subscribe({
      next: (data) => {
        this.projects = data.filter(p => p.active);
        this.projectsLoaded = true;
      },
      error: (err) => console.error('Fehler beim Laden der Projekte:', err)
    });
  }

  /**
   * Lädt die Kunden für das Dropdown (nur id und name, alphabetisch sortiert)
   */
  private loadCustomers(): void {
    if (this.customersLoaded) return;
    this.customerService.loadCustomers().subscribe({
      next: (data) => {
        this.customers = data.sort((a, b) => a.name.localeCompare(b.name));
        this.customersLoaded = true;
      },
      error: (err) => console.error('Fehler beim Laden der Kunden:', err)
    });
  }

  /**
   * Startet das Inline-Editing für ein Feld
   */
  startEditingField(field: string): void {
    if (this.delivery?.status === 'cancelled') return;

    // Vorherige Bearbeitung abbrechen
    this.cancelEditingField();

    this.editingField = field;

    switch (field) {
      case 'ticket':
        this.editTicketNumber = this.delivery?.ticket_order_nr || '';
        this.ticketCheckStatus = this.delivery?.ticket_id ? 'found' : 'idle';
        this.foundTicketId = this.delivery?.ticket_id || null;
        break;
      case 'project':
        this.loadProjects();
        this.editProjectId = this.delivery?.project_id || null;
        break;
      case 'customer':
        this.loadCustomers();
        this.editCustomerId = this.delivery?.customer_id || null;
        break;
      case 'packaging_type':
        this.editPackagingType = this.delivery?.packaging_type || '';
        break;
      case 'order_date':
        this.editOrderDate = this.delivery?.order_date ? new Date(this.delivery.order_date) : null;
        break;
    }
  }

  /**
   * Bricht das Inline-Editing ab
   */
  cancelEditingField(): void {
    this.editingField = null;
    this.ticketCheckStatus = 'idle';
    this.foundTicketId = null;
    this.foundTicketOrderNr = null;
  }

  /**
   * Speichert ein einzelnes Feld
   */
  saveField(field: string): void {
    if (!this.delivery) return;

    this.isSavingField = true;
    const updateData: any = {};

    switch (field) {
      case 'ticket':
        updateData.ticket_id = this.foundTicketId;
        break;
      case 'project':
        updateData.project_id = this.editProjectId;
        break;
      case 'customer':
        updateData.customer_id = this.editCustomerId;
        break;
      case 'packaging_type':
        updateData.packaging_type = this.editPackagingType || null;
        break;
      case 'order_date':
        updateData.order_date = this.formatDate(this.editOrderDate);
        break;
    }

    this.expectedDeliveryService.update(this.delivery.id, updateData).subscribe({
      next: (response) => {
        this.delivery = response.expected_delivery;
        this.editingField = null;
        this.isSavingField = false;
      },
      error: (err) => {
        this.errorMessage = err.error?.error || err.error?.errors?.join(', ') || 'Fehler beim Speichern.';
        this.isSavingField = false;
      }
    });
  }

  /**
   * Prüft ob ein Ticket existiert (für Ticket-Feld)
   */
  checkTicket(): void {
    const ticketNr = this.editTicketNumber.trim();

    if (!ticketNr) {
      this.ticketCheckStatus = 'idle';
      this.foundTicketId = null;
      this.foundTicketOrderNr = null;
      return;
    }

    this.ticketCheckStatus = 'checking';

    this.ticketService.findTicketGroupByOrderNr(ticketNr).subscribe({
      next: (response) => {
        this.ticketCheckStatus = 'found';
        this.foundTicketId = response.ticket.id;
        this.foundTicketOrderNr = response.ticket.order_nr;
      },
      error: () => {
        this.ticketCheckStatus = 'not_found';
        this.foundTicketId = null;
        this.foundTicketOrderNr = null;
      }
    });
  }

  /**
   * Wird aufgerufen wenn sich die Ticket-Nummer ändert
   */
  onTicketNumberChange(): void {
    if (this.ticketCheckStatus !== 'idle') {
      this.ticketCheckStatus = 'idle';
      this.foundTicketId = null;
      this.foundTicketOrderNr = null;
    }
  }

  /**
   * Löscht die Ticket-Zuordnung
   */
  clearTicket(): void {
    this.editTicketNumber = '';
    this.ticketCheckStatus = 'idle';
    this.foundTicketId = null;
    this.foundTicketOrderNr = null;
  }

  // Artikel Modal

  /**
   * Öffnet das Modal mit der Artikel-Liste für einen Wareneingang
   */
  showArticlesModal(receipt: ExpectedDeliveryReceipt): void {
    this.selectedReceipt = receipt;
  }

  // Approval Management

  /**
   * Prüft ob die Lieferung auf Freigabe wartet (unexpected status)
   */
  isUnexpected(): boolean {
    return this.delivery?.status === DELIVERY_STATUS.UNEXPECTED;
  }

  /**
   * Prüft ob die Lieferung auf Bestätigung wartet (pending_confirmation status)
   */
  isPendingConfirmation(): boolean {
    return this.delivery?.status === DELIVERY_STATUS.PENDING_CONFIRMATION;
  }

  /**
   * Gibt die URL für den PDF-Download zurück
   */
  /**
   * Gibt die Download-URL für ein spezifisches PDF zurück
   */
  getDeliveryNotePdfUrl(filename: string): string {
    if (!this.delivery) return '';
    return this.expectedDeliveryService.getDeliveryNotePdfUrl(this.delivery.id, filename);
  }

  /**
   * Prüft ob mindestens ein PDF vorhanden ist
   */
  hasPdfs(): boolean {
    return (this.delivery?.delivery_note_pdfs?.length ?? 0) > 0;
  }

  /**
   * Öffnet den Freigabe-Dialog
   */
  openApprovalDialog(): void {
    this.showApprovalDialog = true;
  }

  /**
   * Schließt den Freigabe-Dialog
   */
  closeApprovalDialog(): void {
    this.showApprovalDialog = false;
  }

  /**
   * Wird aufgerufen wenn die Lieferung erfolgreich freigegeben wurde
   */
  onDeliveryApproved(updatedDelivery: ExpectedDelivery): void {
    this.delivery = updatedDelivery;
    this.showApprovalDialog = false;
    // Historie neu laden
    this.loadHistory(String(updatedDelivery.id));
  }

  // Item Dialog Management

  /**
   * Prüft ob Items bearbeitet werden können
   * Bei Status "open", "partial" oder "completed" (Backend erlaubt Item-Änderungen und berechnet Status neu)
   */
  canEditItems(): boolean {
    if (!this.delivery) return false;
    return this.delivery.status === DELIVERY_STATUS.OPEN
        || this.delivery.status === DELIVERY_STATUS.PARTIAL
        || this.delivery.status === DELIVERY_STATUS.COMPLETED;
  }

  /**
   * Öffnet den Dialog zum Bearbeiten einer Position
   */
  openEditItemDialog(item: ExpectedDeliveryItem): void {
    if (!this.canEditItems()) return;
    this.selectedItem = item;
    this.itemDialogMode = 'edit';
    this.showItemDialog = true;
  }

  /**
   * Öffnet den Dialog zum Erstellen einer neuen Position
   */
  openCreateItemDialog(): void {
    if (!this.canEditItems()) return;
    this.selectedItem = null;
    this.itemDialogMode = 'create';
    this.showItemDialog = true;
  }

  /**
   * Schließt den Item-Dialog
   */
  closeItemDialog(): void {
    this.showItemDialog = false;
    this.selectedItem = null;
  }

  /**
   * Speichert eine neue oder bearbeitete Position
   */
  onItemSaved(data: ItemDialogSaveData): void {
    if (!this.delivery) return;

    this.isSavingItem = true;

    // Für Update: expected_delivery_items_attributes mit der einen Position
    const itemsAttributes = [{
      id: data.id, // undefined bei create
      article_template_id: data.article_template_id,
      quantity_ordered: data.quantity_ordered,
      owner: data.owner
    }];

    this.expectedDeliveryService.update(this.delivery.id, {
      expected_delivery_items_attributes: itemsAttributes
    }).subscribe({
      next: (response) => {
        this.delivery = response.expected_delivery;
        this.isSavingItem = false;
        this.closeItemDialog();
      },
      error: (err) => {
        this.errorMessage = err.error?.error || err.error?.errors?.join(', ') || 'Fehler beim Speichern.';
        this.isSavingItem = false;
      }
    });
  }

  /**
   * Löscht eine Position
   */
  onItemDeleted(itemId: number): void {
    if (!this.delivery) return;

    this.isSavingItem = true;

    // Für Delete: _destroy: true setzen
    const itemsAttributes = [{
      id: itemId,
      _destroy: true
    }];

    this.expectedDeliveryService.update(this.delivery.id, {
      expected_delivery_items_attributes: itemsAttributes as any
    }).subscribe({
      next: (response) => {
        this.delivery = response.expected_delivery;
        this.isSavingItem = false;
        this.closeItemDialog();
      },
      error: (err) => {
        this.errorMessage = err.error?.error || err.error?.errors?.join(', ') || 'Fehler beim Löschen.';
        this.isSavingItem = false;
      }
    });
  }

  /**
   * Gibt das Status-Badge für eine Position zurück
   */
  getItemStatusBadge(item: ExpectedDeliveryItem): { class: string; label: string } {
    if (item.quantity_received === 0) {
      return { class: 'badge-info', label: 'Offen' };
    } else if (item.quantity_received >= item.quantity_ordered) {
      return { class: 'badge-success', label: 'Vollständig' };
    } else {
      return { class: 'badge-warning', label: 'Teilweise' };
    }
  }
}
